import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, UserPlus, KeyRound, Check, Wallet, Save } from "lucide-react";
import { api, apiError, fmtHours } from "@/lib/api";

export default function AdminOperators() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", pin: "" });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({ name: "", pin: "" });
  const [balances, setBalances] = useState({});
  const [balEdit, setBalEdit] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/operators");
      setList(data);
      const operators = data.filter((o) => o.role === "operator");
      const bals = await Promise.all(
        operators.map((o) => api.get(`/operators/${o.id}/balance`).then((r) => [o.id, r.data]).catch(() => [o.id, null]))
      );
      setBalances(Object.fromEntries(bals));
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/operators", form);
      toast.success("Operatore aggiunto");
      setForm({ name: "", pin: "" });
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (id) => {
    const payload = {};
    if (edit.name) payload.name = edit.name;
    if (edit.pin) payload.pin = edit.pin;
    try {
      await api.put(`/operators/${id}`, payload);
      toast.success("Operatore aggiornato");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveBalance = async (id) => {
    const b = balEdit[id];
    if (!b) return;
    try {
      await api.put(`/operators/${id}`, {
        annual_vacation_days: Number(b.vacation_days),
        annual_permit_hours: Number(b.permit_hours),
        hour_bank_minutes: Math.round(Number(b.bank_hours) * 60),
      });
      toast.success("Monte aggiornato");
      setBalEdit((prev) => { const n = { ...prev }; delete n[id]; return n; });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startBalanceEdit = (op) => {
    const b = balances[op.id];
    setBalEdit((prev) => ({
      ...prev,
      [op.id]: {
        vacation_days: b?.vacation.total_days ?? op.annual_vacation_days ?? 26,
        permit_hours: b?.permit.total_hours ?? op.annual_permit_hours ?? 104,
        bank_hours: b?.recovery.bank_hours ?? 0,
      },
    }));
  };

  const remove = async (id) => {
    try {
      await api.delete(`/operators/${id}`);
      toast.success("Operatore eliminato");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggle = async (op) => {
    try {
      await api.put(`/operators/${op.id}`, { active: !op.active });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-operators">
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Operatori</h1>

      <form onSubmit={create} className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-4 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">Nome e cognome</label>
          <input
            data-testid="operator-name-input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            placeholder="Maria Rossi"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-gray-600">PIN (4 cifre)</label>
          <input
            data-testid="operator-pin-input"
            required
            inputMode="numeric"
            maxLength={4}
            value={form.pin}
            onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            className="mt-1 w-full h-11 rounded-lg border border-gray-200 px-3 text-sm font-mono-num focus:ring-2 focus:ring-emerald-500 focus:outline-none"
            placeholder="1234"
          />
        </div>
        <button
          data-testid="operator-create-btn"
          disabled={busy}
          className="h-11 px-6 rounded-lg bg-emerald-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors hover:bg-emerald-700 active:scale-95 disabled:opacity-60"
        >
          <UserPlus className="h-4 w-4" /> Aggiungi
        </button>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-gray-600">
              <th className="pb-3">Nome</th><th className="pb-3">Ruolo</th><th className="pb-3">Stato</th><th className="pb-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {list.map((op) => (
              <tr key={op.id} data-testid={`operator-row-${op.id}`} className="border-t border-gray-100">
                <td className="py-3 font-medium">
                  {editing === op.id ? (
                    <div className="flex gap-2">
                      <input
                        data-testid={`operator-edit-name-${op.id}`}
                        value={edit.name}
                        onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                        className="h-9 w-40 rounded-lg border border-gray-200 px-2 text-sm"
                      />
                      <input
                        data-testid={`operator-edit-pin-${op.id}`}
                        placeholder="nuovo PIN"
                        maxLength={4}
                        value={edit.pin}
                        onChange={(e) => setEdit({ ...edit, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                        className="h-9 w-28 rounded-lg border border-gray-200 px-2 text-sm font-mono-num"
                      />
                    </div>
                  ) : (
                    op.name
                  )}
                </td>
                <td className="py-3 text-gray-600">{op.role === "owner" ? "Titolare" : "Operatore"}</td>
                <td className="py-3">
                  <button
                    data-testid={`operator-toggle-${op.id}`}
                    onClick={() => toggle(op)}
                    className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${op.active ? "bg-emerald-500/10 text-emerald-700" : "bg-gray-200 text-gray-600"}`}
                  >
                    {op.active ? "Attivo" : "Disattivo"}
                  </button>
                </td>
                <td className="py-3">
                  <div className="flex justify-end gap-2">
                    {editing === op.id ? (
                      <button
                        data-testid={`operator-save-${op.id}`}
                        onClick={() => saveEdit(op.id)}
                        className="h-9 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 transition-colors hover:bg-emerald-700"
                      >
                        <Check className="h-3.5 w-3.5" /> Salva
                      </button>
                    ) : (
                      <button
                        data-testid={`operator-edit-${op.id}`}
                        onClick={() => { setEditing(op.id); setEdit({ name: op.name, pin: "" }); }}
                        className="h-9 px-3 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 flex items-center gap-1 transition-colors hover:bg-gray-50"
                      >
                        <KeyRound className="h-3.5 w-3.5" /> Modifica
                      </button>
                    )}
                    {op.role !== "owner" && (
                      <button
                        data-testid={`operator-delete-${op.id}`}
                        onClick={() => remove(op.id)}
                        className="h-9 px-3 rounded-lg border border-red-200 text-red-600 text-xs font-semibold flex items-center gap-1 transition-colors hover:bg-red-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Elimina
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-testid="balances-panel">
        <div className="flex items-center gap-2 mb-1">
          <Wallet className="h-4 w-4 text-emerald-600" />
          <h2 className="text-base font-semibold">Monte ferie e permessi</h2>
        </div>
        <p className="text-xs text-gray-500">Modifica il monte annuo di ferie (giorni) e permessi (ore) per ogni operatore. La banca ore serve per i giorni di recupero.</p>
        {list.filter((o) => o.role === "operator").length === 0 ? (
          <p className="text-sm text-gray-600 mt-4">Nessun operatore registrato.</p>
        ) : (
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {list.filter((o) => o.role === "operator").map((op) => {
              const b = balances[op.id];
              const isEdit = !!balEdit[op.id];
              return (
                <div key={op.id} data-testid={`balance-card-${op.id}`} className="rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">{op.name}</h3>
                    {!isEdit ? (
                      <button
                        data-testid={`balance-edit-${op.id}`}
                        onClick={() => startBalanceEdit(op)}
                        className="text-xs font-semibold text-emerald-700 hover:underline"
                      >
                        Modifica
                      </button>
                    ) : (
                      <button
                        data-testid={`balance-save-${op.id}`}
                        onClick={() => saveBalance(op.id)}
                        className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1"
                      >
                        <Save className="h-3 w-3" /> Salva
                      </button>
                    )}
                  </div>
                  {isEdit ? (
                    <div className="mt-4 grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs text-gray-500">Ferie (gg)</label>
                        <input
                          data-testid={`balance-vac-${op.id}`}
                          type="number"
                          min="0"
                          max="365"
                          value={balEdit[op.id].vacation_days}
                          onChange={(e) => setBalEdit({ ...balEdit, [op.id]: { ...balEdit[op.id], vacation_days: e.target.value } })}
                          className="mt-1 w-full h-10 rounded-lg border border-gray-200 px-2 text-sm font-mono-num"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Permessi (h)</label>
                        <input
                          data-testid={`balance-perm-${op.id}`}
                          type="number"
                          min="0"
                          max="2000"
                          value={balEdit[op.id].permit_hours}
                          onChange={(e) => setBalEdit({ ...balEdit, [op.id]: { ...balEdit[op.id], permit_hours: e.target.value } })}
                          className="mt-1 w-full h-10 rounded-lg border border-gray-200 px-2 text-sm font-mono-num"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Banca ore</label>
                        <input
                          data-testid={`balance-bank-${op.id}`}
                          type="number"
                          step="0.5"
                          min="0"
                          value={balEdit[op.id].bank_hours}
                          onChange={(e) => setBalEdit({ ...balEdit, [op.id]: { ...balEdit[op.id], bank_hours: e.target.value } })}
                          className="mt-1 w-full h-10 rounded-lg border border-gray-200 px-2 text-sm font-mono-num"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-gray-500">Ferie</p>
                        <p data-testid={`balance-vac-view-${op.id}`} className="font-mono-num font-bold text-lg mt-1">
                          {b ? `${b.vacation.residual_days}/${b.vacation.total_days}` : "—"}
                          <span className="text-xs text-gray-500 ml-1">gg</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Permessi</p>
                        <p data-testid={`balance-perm-view-${op.id}`} className="font-mono-num font-bold text-lg mt-1">
                          {b ? `${b.permit.residual_hours}/${b.permit.total_hours}` : "—"}
                          <span className="text-xs text-gray-500 ml-1">h</span>
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Banca ore</p>
                        <p data-testid={`balance-bank-view-${op.id}`} className="font-mono-num font-bold text-lg mt-1">
                          {b ? fmtHours(b.recovery.bank_minutes) : "—"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
